import { useState } from 'react';
import { tv } from 'tailwind-variants';
import { COMMUNITY_GUIDELINES_URL } from 'features/pages/slice';
import { MIN_CONTENT_LENGTH } from 'features/comments/slice';

import {
  Button,
  Form,
  InputWrapper,
  Label,
  MarkdownPreview,
  Modal,
  useModal,
} from '@frontendmentorio/design-system-v2';

const styles = tv({
  base: [
    'bg-gray-100',
    'flex',
    'flex-col',
    'gap-4',
    'p-4',
    'rounded-xl',
    'w-full',
  ],
  variants: {
    withHelpText: { true: ['desktop:basis-[70%]'] },
  },
});

export default function FeedbackForm({
  comment,
  onSubmit,
  user,
  withHelpText,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const modal = useModal();

  const handleBeforeValidate = (data) => {
    if (data.content && data.content.length < MIN_CONTENT_LENGTH) {
      modal.open();

      return false;
    }
  };

  const handleSubmit = async (data, state, methods) => {
    setIsLoading(true);
    await onSubmit(data, state, methods);
    setIsLoading(false);
  };

  return (
    <Form onBeforeValidate={handleBeforeValidate} onSubmit={handleSubmit}>
      <Label
        className="col-span-2 font-normal text-xl"
        htmlFor="feedback-content"
      >
        Leave your feedback
      </Label>

      <div className="flex flex-col-reverse gap-5 items-start desktop:flex-row">
        <div className={styles({ withHelpText })}>
          <InputWrapper
            ariaDescribedby={withHelpText ? 'feedback-help' : undefined}
            defaultValue={comment?.content}
            input={MarkdownPreview}
            id="feedback-content"
            name="content"
            validations={{ required: true }}
          />

          <Button
            className="self-end"
            disabled={isLoading}
            isWide
            level="primary"
          >
            {isLoading ? 'Submitting...' : 'Post feedback'}
          </Button>
        </div>

        {withHelpText && (
          <div
            className="border-neutral-200 flex flex-col gap-2 rounded-xl desktop:basis-[30%] desktop:bg-neutral-50 desktop:border desktop:p-4"
            id="feedback-help"
          >
            <p className="font-semibold">
              Provide quality feedback and answer {user.displayName}’s
              questions. Key points to consider:
            </p>

            <ul className="flex flex-col gap-2 italic list-disc pl-3 text-neutral-500 text-sm">
              <li>Does the solution include semantic HTML?</li>
              <li>Is it accessible, and what improvements could be made?</li>
              <li>Does the layout look good on a range of screen sizes?</li>
              <li>Is the code well-structured, readable, and reusable?</li>
              <li>Does the solution differ considerably from the design?</li>
            </ul>
          </div>
        )}
      </div>

      <Modal modal={modal}>
        <Modal.Heading>This looks like a short comment</Modal.Heading>

        <p>
          Please ensure you’re providing helpful feedback and answering any
          questions the solution author might have. You can{' '}
          <a href={COMMUNITY_GUIDELINES_URL}>read our community guidelines</a>{' '}
          if you’re unsure what to post.
        </p>

        <div className="flex gap-2 items-center justify-end">
          <Button isGhost level="secondary" onClick={modal.close}>
            Cancel
          </Button>

          <Form.Button
            onClick={(data, state, methods) => {
              handleSubmit(data, state, methods);
              modal.close();
            }}
          >
            I understand, post anyway
          </Form.Button>
        </div>
      </Modal>
    </Form>
  );
}
